import React, { useEffect, useRef, useState } from "react";
import { ISirTable, ISirTableData, ISirTableDataResponse } from "../../../interfaces/invoice";
import { InvoiceServices } from "../../../services/invoice";
import { useMessageApi } from "../../../context/MessageContext";
import { Button, Col, DatePicker, Flex, Form, Input, Row, Switch, Table, Modal, Select, Checkbox } from "antd";
import type { ColumnsType } from "antd/es/table";
import { CheckOutlined, CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { BOOKING_NO_PATTERN, DATE_FORMAT, DATE_FORMAT_API } from "../../../constants/common";
import dayjs from "dayjs";
import { renderClassNameStatusInvoice, renderClassNameStatusSir, roundCost } from "../../../utils/helper";
import { ISuppliersByNameResult } from "../../../interfaces/supplier";
import FormattedNumberInput from "../../../components/format-number-input";
import { InvoiceStatusEnum, SupplierInvoiceRecordStatusEnum } from "../../../constants/enum";

const defaultRow = {
  status: "",
  bookingNo: undefined,
  amount: undefined,
  leadPax: undefined,
  isActive: true,
  reason: "",
};

interface CreateAndEditInvoiceProps {
  isVisible: boolean;
  onClose: (okTriggered?: boolean) => void;
  id: string;
  isEdit?: boolean;
  currencies: any[];
  suppliers: ISuppliersByNameResult[];
  isFlight?: boolean;
  isPermissionEdit?: boolean;
}

const CreateAndEditInvoice: React.FC<CreateAndEditInvoiceProps> = ({
  isVisible,
  onClose,
  id,
  isEdit = true,
  currencies,
  suppliers,
  isFlight = false,
  isPermissionEdit,
}) => {
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const messageApi = useMessageApi();
  const [form] = Form.useForm();
  const supplierInvoiceRecords = Form.useWatch("supplierInvoiceRecords", form);
  const textCreateOrEdit = isEdit ? "Update" : "Create";

  const tableRef = useRef<HTMLDivElement>(null);
  const isNewRowAddedRef = useRef(false);
  const [bookingFormatError, setBookingFormatError] = useState<string | null>(null);
  const [originalSupplierId, setOriginalSupplierId] = useState<string>();

  useEffect(() => {
    form.resetFields();
    const fetchData = async () => {
      try {
        setLoading(true);
        const response: ISirTableDataResponse = await InvoiceServices.getInvoiceDetail(id);
        const { success, data } = response;
        if (success) {
          // Store original values for comparison
          setOriginalSupplierId(data.supplierId);
          form.setFieldsValue({
            ...data,
            ...(isFlight
              ? {
                  transactionDate: data.transactionDate ? dayjs(data.transactionDate) : null,
                }
              : {
                  invoiceDate: data.invoiceDate ? dayjs(data.invoiceDate) : null,
                  receiveDate: data.receiveDate ? dayjs(data.receiveDate) : null,
                }),

            supplierInvoiceRecords: [
              ...data.supplierInvoiceRecords.map((sir: any) => ({
                ...sir,
                ...(!!sir?.invoiceMatchId && { bookingNo: sir?.bookingNo ? sir?.bookingNo + "-CR" : "CR" }),
              })),
              ...(!isEdit ? [defaultRow] : []),
            ],
          });
        }
      } catch (error) {
        console.error("Fetch error:", error);
      } finally {
        setLoading(false);
      }
    };
    if (isVisible) {
      fetchData();
    }
  }, [isVisible, id, form, isEdit, isFlight]);

  useEffect(() => {
    if (isNewRowAddedRef.current && tableRef.current) {
      const body = tableRef.current.querySelector(".ant-table-body");
      if (body) {
        body.scrollTop = body.scrollHeight;
      }
      isNewRowAddedRef.current = false;
    }
  }, [supplierInvoiceRecords]);

  const onFinish = async (values: ISirTableData) => {
    try {
      const sirTotalAmount = roundCost(
        (values.supplierInvoiceRecords || [])
          .filter((r: ISirTable) => r.isActive)
          .reduce((sum: number, item: any) => sum + (item.amount || 0), 0)
      );
      const totalAmount = roundCost(values?.totalAmount || 0);

      if (totalAmount && totalAmount !== sirTotalAmount) {
        messageApi.error(
          `Cost mismatched between SIR and Invoice, the remaining cost is ${(totalAmount - sirTotalAmount).toFixed(2)}`
        );
        return;
      }

      const isValidAmountCreditNote = (values.supplierInvoiceRecords || []).some((sir: any) =>
        requiredAmountCreditNote(sir)
      );

      if (isValidAmountCreditNote) {
        messageApi.error(`Credit note must be negative amount`);
        return;
      }

      setIsLoadingSubmit(true);
      const body = {
        ...values,
        ...(isFlight
          ? { transactionDate: dayjs(values.transactionDate).format(DATE_FORMAT_API) }
          : { invoiceDate: dayjs(values.invoiceDate).format(DATE_FORMAT_API) }),
        supplierInvoiceRecords: supplierInvoiceRecords.map((item: ISirTable) => ({
          ...item,
          reason: item?.reason || "",
          bookingNo: item?.bookingNo?.replace(/-?CR$/, ""),
        })),
      };
      if (isFlight) {
        await InvoiceServices.updateInvoiceFlightDetail(id, body);
      } else await InvoiceServices.updateInvoiceDetail(id, body);

      messageApi.success(
        `${textCreateOrEdit} Invoice / Supplier Invoice Record ${isFlight ? "Flight" : ""} successfully!`
      );
      onClose(true);
    } catch (err: any) {
      const message =
        err.message ?? `${textCreateOrEdit} Invoice / Supplier Invoice Record ${isFlight ? "Flight" : ""} failed!`;
      messageApi.error(message);
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    const isValidFormatBookingNo = errorInfo?.values?.supplierInvoiceRecords.some((sir: any) =>
      validFormatBooking(sir)
    );
    if (isValidFormatBookingNo) {
      setBookingFormatError("Invalid Booking Reference ID");
    }
  };

  const filterSelectOption = (input: string, option?: { label: string; name: string }) => {
    const label = option?.label?.toString().toLowerCase() ?? "";
    const name = option?.name?.toLowerCase?.() ?? "";

    return label.includes(input.toLowerCase()) || name.includes(input.toLowerCase());
  };

  const optionRender = (option: any) => <>{`${option.data.label} - ${option.data.name}`}</>;

  const handleChangeSupplierCode = (value: string) => {
    const find = suppliers.find((item) => item.key === value);
    form.setFieldValue("supplierName", find?.label);
    form.setFieldValue("currency", find?.currency);
  };

  const requiredBookingOrLeadPax = (row: ISirTable) => {
    const { bookingNo, leadPax } = row || {};
    if (!isCheckDisabledRow(row) && !bookingNo && !leadPax) {
      return true;
    }
    return false;
  };

  const validFormatBooking = (row: ISirTable) => {
    const { bookingNo } = row || {};
    if (!isCheckDisabledRow(row)) {
      if (bookingNo && !BOOKING_NO_PATTERN.test(bookingNo)) {
        return true;
      }
    }
    return false;
  };

  const requiredAmountCreditNote = (row: ISirTable) => {
    const { invoiceMatchId, amount } = row || {};
    if (!isCheckDisabledRow(row) && invoiceMatchId && Number(amount) >= 0) {
      return true;
    }
    return false;
  };

  const isCheckDisabledRow = (row: ISirTable) => {
    const { isActive, status } = row || {};
    if (!isActive || status === SupplierInvoiceRecordStatusEnum.EXPORTED) {
      return true;
    }
    return false;
  };

  const isCredit = (index: number) => {
    return !!supplierInvoiceRecords?.[index]?.invoiceMatchId;
  };

  const setValueFieldBySirRefId = (row: ISirTable, field: string, value: string | boolean) => {
    const { id, invoiceMatchId } = row || {};
    for (let i = 0; i < supplierInvoiceRecords?.length; i++) {
      if (id && !invoiceMatchId && supplierInvoiceRecords?.[i]?.sirRefId === id) {
        form.setFieldValue(
          ["supplierInvoiceRecords", i, field],
          field === "bookingNo" ? `${value ? value + "-CR" : "CR"}` : value
        );
      }
    }
  };

  const isInvoiceDisabled = () => {
    if (isFlight) {
      const { status } = supplierInvoiceRecords?.[0] || {};
      if (status === SupplierInvoiceRecordStatusEnum.EXPORTED) {
        return true;
      }
      return false;
    } else {
      return form?.getFieldValue("status") === InvoiceStatusEnum.Exported;
    }
  };

  return (
    <Modal
      open={isVisible}
      onCancel={() => onClose()}
      title={`${textCreateOrEdit} Supplier Invoice Record ${isFlight ? "Flight" : ""}`}
      footer={null}
      width={1040}
    >
      <Form
        form={form}
        layout="horizontal"
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        disabled={!isPermissionEdit}
      >
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label="Supplier Code"
              name="supplierId"
              labelCol={{ span: 9 }}
              rules={[{ required: true, message: "Complete this required field." }]}
            >
              <Select
                showSearch
                allowClear
                filterOption={filterSelectOption}
                options={suppliers.map((item) => ({
                  value: item.key,
                  label: item.code,
                  name: item.label,
                }))}
                placeholder="Select"
                optionRender={optionRender}
                onChange={handleChangeSupplierCode}
                disabled={isInvoiceDisabled()}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Supplier Name"
              name="supplierName"
              labelCol={{ span: 9 }}
              rules={[{ required: true, message: "Complete this required field." }]}
            >
              <Input disabled />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Invoice Number"
              name="invoiceNumber"
              labelCol={{ span: 9 }}
              rules={[{ required: isFlight, message: "Complete this required field." }]}
            >
              <Input disabled={isInvoiceDisabled()} />
            </Form.Item>
          </Col>
          {!isFlight && (
            <>
              <Col span={12}>
                <Form.Item label="Receive Date" name="receiveDate" labelCol={{ span: 9 }}>
                  <DatePicker className="w-full" format={DATE_FORMAT} placeholder="Select date" disabled />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item shouldUpdate noStyle>
                  {({ getFieldValue }) => {
                    const status = getFieldValue("status");
                    return (
                      <Form.Item label="Status" name="status" labelCol={{ span: 9 }}>
                        <Input disabled className={renderClassNameStatusInvoice(status)} />
                      </Form.Item>
                    );
                  }}
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Invoice Date"
                  name="invoiceDate"
                  labelCol={{ span: 9 }}
                  rules={[{ required: true, message: "Complete this required field." }]}
                >
                  <DatePicker
                    className="w-full"
                    format={DATE_FORMAT}
                    placeholder="Select date"
                    readOnly
                    disabled={isInvoiceDisabled()}
                  />
                </Form.Item>
              </Col>
            </>
          )}
          {isFlight && (
            <Col span={12}>
              <Form.Item
                label="Transaction Date"
                name="transactionDate"
                labelCol={{ span: 9 }}
                rules={[{ required: true, message: "Complete this required field." }]}
              >
                <DatePicker
                  className="w-full"
                  format={DATE_FORMAT}
                  placeholder="Select date"
                  readOnly
                  disabled={isInvoiceDisabled()}
                />
              </Form.Item>
            </Col>
          )}
          <Col span={12}>
            <Form.Item
              label="Currency"
              name="currency"
              labelCol={{ span: 9 }}
              rules={[{ required: true, message: "Complete this required field." }]}
            >
              <Input readOnly disabled={true} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="Total Invoice Amount"
              name="totalAmount"
              labelCol={{ span: 9 }}
              rules={[{ required: true, message: "Complete this required field." }]}
            >
              <FormattedNumberInput
                className="w-full"
                disabled={isFlight || !isPermissionEdit || isInvoiceDisabled()}
              />
            </Form.Item>
          </Col>
        </Row>
        {!isFlight && (
          <Form.Item shouldUpdate={(prev, curr) => prev.aiLimitation !== curr.aiLimitation} noStyle>
            {({ getFieldValue, setFieldValue }) => {
              const isChecked = getFieldValue("aiLimitation");
              return (
                <Row align="middle" gutter={16}>
                  <Col flex="none">
                    <Form.Item name="aiLimitation" valuePropName="checked" noStyle>
                      <Checkbox
                        disabled={isInvoiceDisabled()}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          if (!checked) {
                            setFieldValue("aiLimitationReason", "");
                          }
                        }}
                      >
                        AI Limitation
                      </Checkbox>
                    </Form.Item>
                  </Col>

                  <Col flex="auto">
                    <Form.Item
                      label="Reason"
                      name="aiLimitationReason"
                      rules={isChecked ? [{ required: true, message: "" }] : []}
                      style={{ marginBottom: 0 }}
                    >
                      <Input
                        placeholder="Enter reason"
                        style={{ width: "100%" }}
                        disabled={!isChecked || isInvoiceDisabled()}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              );
            }}
          </Form.Item>
        )}

        {!loading && !isFlight && (
          <Form.Item shouldUpdate noStyle>
            {({ getFieldValue }) => {
              const totalAmount = getFieldValue("totalAmount") || 0;
              const sirTables = getFieldValue(["supplierInvoiceRecords"]) || [];
              const sirTotalAmount = sirTables
                .filter((r: ISirTable) => r.isActive)
                .reduce((sum: number, item: any) => sum + (item.amount || 0), 0);

              const isValidBookingOrLeadPax = sirTables.some((sir: any) => requiredBookingOrLeadPax(sir));

              const isValidAmountCreditNote = sirTables.some((sir: any) => requiredAmountCreditNote(sir));

              return (
                <div className="text-error" style={{ textAlign: "center", margin: "10px 0" }}>
                  {roundCost(totalAmount) !== roundCost(sirTotalAmount) && (
                    <div>{`Cost mismatched between SIR and Invoice, the remaining cost is ${(
                      totalAmount - sirTotalAmount
                    ).toFixed(2)}`}</div>
                  )}
                  {isValidBookingOrLeadPax && (
                    <div>
                      Please fill out the <strong>Booking Reference ID</strong> or <strong>Lead Passenger</strong>{" "}
                      fields before saving the invoice record
                    </div>
                  )}
                  {isValidAmountCreditNote && <div>Credit note must be negative amount</div>}
                </div>
              );
            }}
          </Form.Item>
        )}

        {bookingFormatError && (
          <div className="text-error" style={{ textAlign: "center", margin: "10px 0" }}>
            {bookingFormatError}
          </div>
        )}

        <Form.List name="supplierInvoiceRecords">
          {(fields, { add }) => {
            const columns: ColumnsType<any> = [
              {
                title: "No.",
                dataIndex: "no",
                width: 45,
                render: (_: any, __: any, index: number) => index + 1,
              },
              {
                title: "Status",
                dataIndex: "status",
                width: 110,
                render: (_: any, record: any) => {
                  const status = supplierInvoiceRecords?.[record.name]?.status;
                  return <span className={renderClassNameStatusSir(status)}>{status}</span>;
                },
              },
              {
                title: "Booking Reference ID",
                dataIndex: "bookingNo",
                width: 180,
                render: (_: any, record: any) => (
                  <Form.Item
                    name={[record.name, "bookingNo"]}
                    rules={[
                      {
                        required: isFlight ? true : requiredBookingOrLeadPax(supplierInvoiceRecords?.[record.name]),
                        message: "",
                      },
                      ...(isCredit(record.name)
                        ? []
                        : [
                            {
                              pattern: BOOKING_NO_PATTERN,
                              message: "",
                              validateTrigger: "onBlur",
                            },
                          ]),
                      {
                        validator: (_, value) => {
                          if (value && !value.trim()) {
                            return Promise.reject(new Error(""));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input
                      placeholder="Enter booking ref"
                      disabled={
                        !isPermissionEdit ||
                        isCheckDisabledRow(supplierInvoiceRecords?.[record.name]) ||
                        isCredit(record.name)
                      }
                      onChange={(e) => {
                        form.validateFields([["supplierInvoiceRecords", record.name, "leadPax"]]);
                        setValueFieldBySirRefId(supplierInvoiceRecords?.[record.name], "bookingNo", e.target.value);
                        setBookingFormatError(null);
                      }}
                    />
                  </Form.Item>
                ),
              },
              ...(isFlight
                ? [
                    {
                      title: "PNR Ref",
                      dataIndex: "pnrRef",
                      width: 180,
                      render: (_: any, record: any) => (
                        <Form.Item name={[record.name, "pnrRef"]}>
                          <Input
                            placeholder="Enter PNR Ref"
                            disabled={
                              !isPermissionEdit ||
                              isCheckDisabledRow(supplierInvoiceRecords?.[record.name]) ||
                              isCredit(record.name)
                            }
                          />
                        </Form.Item>
                      ),
                    },
                  ]
                : [
                    {
                      title: "Lead Passenger",
                      dataIndex: "leadPax",
                      width: 180,
                      render: (_: any, record: any) => (
                        <Form.Item
                          name={[record.name, "leadPax"]}
                          rules={[
                            { required: requiredBookingOrLeadPax(supplierInvoiceRecords?.[record.name]), message: "" },
                            {
                              validator: (_, value) => {
                                if (value && !value.trim()) {
                                  return Promise.reject(new Error(""));
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <Input
                            placeholder="Enter Lead Passenger"
                            disabled={
                              !isPermissionEdit ||
                              isCheckDisabledRow(supplierInvoiceRecords?.[record.name]) ||
                              isCredit(record.name)
                            }
                            onChange={(e) => {
                              form.validateFields([["supplierInvoiceRecords", record.name, "bookingNo"]]);
                              setValueFieldBySirRefId(supplierInvoiceRecords?.[record.name], "leadPax", e.target.value);
                            }}
                          />
                        </Form.Item>
                      ),
                    },
                  ]),
              {
                title: "Amount",
                dataIndex: "amount",
                width: 140,
                render: (_: any, record: any) => (
                  <Form.Item
                    name={[record.name, "amount"]}
                    rules={[{ required: supplierInvoiceRecords?.[record.name]?.isActive, message: "" }]}
                  >
                    <FormattedNumberInput
                      className="w-full input-t-r"
                      placeholder="Enter amount"
                      disabled={!isPermissionEdit || isCheckDisabledRow(supplierInvoiceRecords?.[record.name])}
                      onChange={(value) => {
                        if (isFlight) {
                          form.setFieldValue("totalAmount", value);
                        }
                      }}
                      {...(isCredit(record.name) &&
                        Number(supplierInvoiceRecords?.[record.name]?.amount) >= 0 && {
                          status: "error",
                        })}
                    />
                  </Form.Item>
                ),
              },
              {
                title: "Active",
                dataIndex: "isActive",
                width: 70,
                align: "center" as const,
                render: (_: any, record: any) => {
                  if (supplierInvoiceRecords?.[record.name]?.id) {
                    return (
                      <Form.Item
                        name={[record.name, "isActive"]}
                        valuePropName="checked"
                        style={{ display: "inline-block" }}
                      >
                        <Switch
                          checkedChildren={<CheckOutlined />}
                          unCheckedChildren={<CloseOutlined />}
                          onChange={(value) => {
                            if (value) {
                              form.setFieldValue(["supplierInvoiceRecords", record.name, "reason"], "");
                            } else {
                              setValueFieldBySirRefId(supplierInvoiceRecords?.[record.name], "isActive", value);
                            }
                            form.validateFields([
                              ["supplierInvoiceRecords", record.name, "bookingNo"],
                              ["supplierInvoiceRecords", record.name, isFlight ? "pnrRef" : "leadPax"],
                            ]);
                            if (isFlight) {
                              form.setFieldValue(
                                "totalAmount",
                                value ? supplierInvoiceRecords?.[record.name]?.amount : 0
                              );
                            }
                          }}
                          disabled={
                            !isPermissionEdit ||
                            supplierInvoiceRecords?.[record.name]?.status === SupplierInvoiceRecordStatusEnum.EXPORTED
                          }
                        />
                      </Form.Item>
                    );
                  }
                  return null;
                },
              },
              {
                title: "Reason",
                dataIndex: "reason",
                width: 200,
                render: (_: any, record: any) => (
                  <Form.Item
                    name={[record.name, "reason"]}
                    rules={[{ required: !supplierInvoiceRecords?.[record.name]?.isActive, message: "" }]}
                  >
                    <Input
                      placeholder={supplierInvoiceRecords?.[record.name]?.isActive ? "" : "Enter Reason"}
                      disabled={supplierInvoiceRecords?.[record.name]?.isActive}
                    />
                  </Form.Item>
                ),
              },
            ];

            return (
              <div ref={tableRef}>
                <Table
                  dataSource={fields}
                  pagination={false}
                  rowKey={(f) => f.key}
                  columns={columns}
                  size="small"
                  className="table-fm"
                  scroll={{ y: 50 * 4 }}
                  loading={loading}
                  rowClassName={(record) => {
                    const classes = [];
                    if (isCredit(record.name)) classes.push("active-credit");

                    return classes.join(" ");
                  }}
                />
                {!isFlight && isPermissionEdit && (
                  <Button
                    color="primary"
                    variant="outlined"
                    block
                    icon={<PlusOutlined />}
                    onClick={() => {
                      isNewRowAddedRef.current = true;
                      add({ ...defaultRow });
                    }}
                    style={{ marginTop: "16px" }}
                    disabled={isInvoiceDisabled()}
                  >
                    Add row
                  </Button>
                )}
              </div>
            );
          }}
        </Form.List>

        <Flex justify="end" gap={16} style={{ marginTop: 20 }}>
          <Form.Item
            shouldUpdate={(prev, curr) => prev.supplierId !== curr.supplierId || prev.status !== curr.status}
            noStyle
          >
            {({ getFieldValue }) => {
              const status = getFieldValue("status");
              const currentSupplierId = getFieldValue("supplierId");
              const shouldShow =
                !isFlight && status === InvoiceStatusEnum.Query && currentSupplierId !== originalSupplierId;

              return shouldShow ? (
                <Form.Item name="bulkUpdateInvoice" valuePropName="checked" initialValue={false} style={{ margin: 0 }}>
                  <Checkbox>Change supplier/currency for all invoices from this attachment</Checkbox>
                </Form.Item>
              ) : null;
            }}
          </Form.Item>
          <Button
            style={{ marginLeft: 8 }}
            onClick={() => onClose()}
            disabled={isLoadingSubmit}
            color="primary"
            variant="outlined"
          >
            Cancel
          </Button>
          {isPermissionEdit && (
            <Button type="primary" htmlType="submit" loading={isLoadingSubmit} disabled={isInvoiceDisabled()}>
              Save
            </Button>
          )}
        </Flex>
      </Form>
    </Modal>
  );
};

export default CreateAndEditInvoice;
